import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { T } from '../utils/theme';
import { fmtBRL, startOfDayCuiaba, endOfDayCuiaba, startOfWeekCuiaba, endOfWeekCuiaba, toCuiabaTimestamp, percentualComissao } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { LargeTitle, SolidCard, SectionHeader, Segmented, HeroCard, KpiTile } from '../components/ui';
import api from '../services/api';
import saqueService from '../services/saqueService';
import { DollarSign, Users, Wallet, ExternalLink } from 'lucide-react';

const TZ_CUIABA = 'America/Cuiaba';

const MinhasComissoesPage = () => {
  const { user } = useAuth();
  const { comandas, configuracoes } = useData();
  const [periodo, setPeriodo] = useState('semana');
  const [saldoDisponivel, setSaldoDisponivel] = useState(0);
  const [showSolicitarSaque, setShowSolicitarSaque] = useState(false);
  const [valorSaque, setValorSaque] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('comissoes');
  const [meusSaques, setMeusSaques] = useState([]);
  const [carregandoSaques, setCarregandoSaques] = useState(false);
  const [selfieCapturada, setSelfieCapturada] = useState(null);
  const [streamVideo, setStreamVideo] = useState(null);
  const [videoRef, setVideoRef] = useState(null);

  useEffect(() => {
    if (!user?.barbeiroId) return undefined;
    carregarSaldo();
    // A baixa costuma ser feita em outro aparelho
    const intervalo = setInterval(carregarSaldo, 30000);
    return () => clearInterval(intervalo);
  }, [user?.barbeiroId]);
  useEffect(() => { if (user?.barbeiroId && abaAtiva === 'extrato') carregarMeusSaques(); }, [user?.barbeiroId, abaAtiva]);

  useEffect(() => {
    if (!user?.barbeiroId) return;
    const handleWalletUpdate = (event) => {
      if (event.detail?.barbeiroId === user.barbeiroId) {
        if (event.detail.saldoDisponivel !== undefined && event.detail.saldoDisponivel !== null) {
          setSaldoDisponivel(event.detail.saldoDisponivel);
        } else { carregarSaldo(); }
      }
    };
    window.addEventListener('walletBalanceUpdated', handleWalletUpdate);
    return () => window.removeEventListener('walletBalanceUpdated', handleWalletUpdate);
  }, [user?.barbeiroId]);

  const carregarSaldo = async () => {
    try { const barbeiro = await api.barbeiros.getById(user.barbeiroId); setSaldoDisponivel(barbeiro.saldoDisponivel || 0); }
    catch { /* falha de rede: mantém o último saldo em vez de mostrar R$ 0,00 */ }
  };

  const carregarMeusSaques = async () => {
    try { setCarregandoSaques(true); const saques = await saqueService.meusSaques(); setMeusSaques(saques || []); }
    catch { setMeusSaques([]); }
    finally { setCarregandoSaques(false); }
  };

  const iniciarCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      setStreamVideo(stream);
      if (videoRef) videoRef.srcObject = stream;
    } catch { alert('Erro ao acessar camera.'); }
  };

  const pararCamera = () => { if (streamVideo) { streamVideo.getTracks().forEach(t => t.stop()); setStreamVideo(null); } };

  const capturarSelfie = () => {
    if (!videoRef) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.videoWidth; canvas.height = videoRef.videoHeight;
    canvas.getContext('2d').drawImage(videoRef, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) { setSelfieCapturada(new File([blob], 'selfie.jpg', { type: 'image/jpeg' })); pararCamera(); }
    }, 'image/jpeg', 0.9);
  };

  const handleSolicitarSaque = async () => {
    const valor = parseFloat(valorSaque);
    if (valor < 100) { alert('Valor minimo: R$ 100,00'); return; }
    if (valor > saldoDisponivel) { alert('Saldo insuficiente'); return; }
    if (!selfieCapturada) { alert('Capture uma selfie'); return; }
    try {
      const formData = new FormData();
      formData.append('valor', valor); formData.append('selfie', selfieCapturada);
      await saqueService.solicitarSaque(formData);
      alert('Solicitacao enviada!');
      setShowSolicitarSaque(false); setValorSaque(''); setSelfieCapturada(null); pararCamera(); carregarSaldo();
    } catch (error) { alert('Erro: ' + (error.response?.data?.error || error.message)); }
  };

  const isBarbeiro = user?.tipo === 'barbeiro';
  const minhasComandas = useMemo(() => {
    if (!isBarbeiro || !user?.barbeiroId) return [];
    return comandas.filter(c => c.barbeiroId === user.barbeiroId && c.status === 'pago');
  }, [isBarbeiro, user?.barbeiroId, comandas]);

  const periodoComissoes = useMemo(() => {
    const hoje = new Date();
    switch (periodo) {
      case 'hoje': return { inicio: startOfDayCuiaba(hoje), fim: endOfDayCuiaba(hoje) };
      case 'semana': return { inicio: startOfWeekCuiaba(hoje), fim: endOfWeekCuiaba(hoje) };
      case 'mes': {
        const f = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_CUIABA, year: 'numeric', month: '2-digit', day: '2-digit' });
        const [y, m] = f.format(hoje).split('-').map(Number);
        return { inicio: new Date(Date.UTC(y, m - 1, 1)), fim: new Date(Date.UTC(y, m, 0, 23, 59, 59, 999)) };
      }
      default: return { inicio: null, fim: null };
    }
  }, [periodo]);

  const comandasFiltradas = useMemo(() => {
    if (!periodoComissoes.inicio || !periodoComissoes.fim) return minhasComandas;
    const inicioMs = periodoComissoes.inicio.getTime(), fimMs = periodoComissoes.fim.getTime();
    return minhasComandas.filter(c => { const ms = toCuiabaTimestamp(c.data); return ms >= inicioMs && ms <= fimMs; });
  }, [minhasComandas, periodoComissoes]);

  if (!isBarbeiro) {
    return <div style={{ padding: 20 }}><SolidCard padding={40} radius={20}><div style={{ textAlign: 'center', color: T.label2 }}>Acesso restrito a barbeiros</div></SolidCard></div>;
  }

  const totalFaturado = comandasFiltradas.reduce((s, c) => s + c.total, 0);
  const totalComissao = comandasFiltradas.reduce((total, c) => {
    if (!c.servicos || !Array.isArray(c.servicos)) return total;
    return total + c.servicos.reduce((sum, s) => {
      const pct = percentualComissao(s, configuracoes.comissaoPadrao);
      return sum + ((s.preco || 0) * pct / 100);
    }, 0);
  }, 0);
  const qtdAtendimentos = comandasFiltradas.length;

  const inputStyle = { width: '100%', padding: '14px 16px', borderRadius: 14, background: T.fill, border: `0.5px solid ${T.separator}`, color: T.label, fontSize: 16, fontFamily: 'inherit', outline: 'none' };

  return (
    <div style={{ padding: '0 0 40px' }}>
      <LargeTitle title="Comissoes" subtitle="Acompanhe seus ganhos" />

      {/* Wallet Card */}
      <div style={{ padding: '0 20px 12px' }}>
        <HeroCard gradient={`linear-gradient(135deg, ${T.purple} 0%, ${T.indigo} 100%)`} shadowColor="rgba(191,90,242,0.35)">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>Minha Carteira</div>
              <div className="tnum" style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.03em', marginTop: 4, lineHeight: 1 }}>{fmtBRL(saldoDisponivel)}</div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>Saldo disponivel</div>
            </div>
            <button onClick={() => setShowSolicitarSaque(true)} style={{
              border: 'none', cursor: 'pointer', padding: '10px 18px', borderRadius: 14,
              background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
              color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
            }}>Solicitar Saque</button>
          </div>
        </HeroCard>
      </div>

      {/* Tabs */}
      <div style={{ padding: '0 20px 12px' }}>
        <Segmented
          items={[{ value: 'comissoes', label: 'Comissoes' }, { value: 'extrato', label: 'Extrato' }]}
          value={abaAtiva} onChange={setAbaAtiva}
        />
      </div>

      {/* Period filter */}
      <div style={{ padding: '0 20px 0' }}>
        <Segmented
          items={[{ value: 'hoje', label: 'Hoje' }, { value: 'semana', label: 'Semana' }, { value: 'mes', label: 'Mes' }]}
          value={periodo} onChange={setPeriodo} size="sm"
        />
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '12px 20px 0' }}>
        <KpiTile icon={Users} tint={T.accent} label="Atendimentos" value={qtdAtendimentos} sub={periodo === 'hoje' ? 'hoje' : periodo === 'semana' ? 'semana' : 'mes'} />
        <KpiTile icon={DollarSign} tint={T.success} label="Faturamento" value={fmtBRL(totalFaturado)} sub="bruto" />
      </div>

      {/* Comissao hero */}
      <div style={{ padding: '12px 20px 0' }}>
        <HeroCard gradient={`linear-gradient(135deg, ${T.success} 0%, #28A745 100%)`} shadowColor="rgba(48,209,88,0.35)">
          <div style={{ fontSize: 13, opacity: 0.85 }}>Minha Comissao</div>
          <div className="tnum" style={{ fontSize: 40, fontWeight: 700, letterSpacing: '-0.03em', marginTop: 4, lineHeight: 1 }}>{fmtBRL(totalComissao)}</div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>Comissao por servico</div>
        </HeroCard>
      </div>

      {/* Content */}
      <div style={{ padding: '0 20px' }}>
        {abaAtiva === 'comissoes' ? (
          <>
            <SectionHeader title="Detalhamento" />
            <SolidCard padding={0} radius={20}>
              {comandasFiltradas.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: T.label3, fontSize: 14 }}>Nenhum atendimento neste periodo</div>
              ) : (
                comandasFiltradas.map((c, i) => {
                  const comissao = (c.servicos && Array.isArray(c.servicos)) ? c.servicos.reduce((sum, s) => {
                    const pct = percentualComissao(s, configuracoes.comissaoPadrao);
                    return sum + ((s.preco || 0) * pct / 100);
                  }, 0) : 0;
                  return (
                    <div key={c.id} style={{ padding: 14, borderBottom: i < comandasFiltradas.length - 1 ? `0.5px solid ${T.separator}` : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: T.label }}>{c.nomeCliente}</div>
                          <div style={{ fontSize: 11, color: T.label3, marginTop: 2 }}>{new Date(c.data).toLocaleDateString('pt-BR')}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12, color: T.label2 }}>Total: {fmtBRL(c.total)}</div>
                          <div className="tnum" style={{ fontSize: 17, fontWeight: 700, color: T.success, marginTop: 2 }}>{fmtBRL(comissao)}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {c.servicos.map((s, j) => (
                          <span key={j} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: T.fill, color: T.label2 }}>{s.nome}</span>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </SolidCard>
          </>
        ) : (
          <>
            <SectionHeader title="Extrato de Movimentacoes" />
            <SolidCard padding={0} radius={20}>
              {carregandoSaques ? (
                <div style={{ padding: 24, textAlign: 'center', color: T.label3 }}>Carregando extrato...</div>
              ) : (() => {
                const extrato = [];
                if (meusSaques && Array.isArray(meusSaques)) {
                  meusSaques.filter(s => s.status === 'pago').forEach(saque => {
                    extrato.push({
                      id: `saque-${saque.id}`, tipo: 'saque',
                      tipoTexto: saque.tipo === 'solicitacao' ? 'Saque Solicitado' : 'Saque do Gestor',
                      valor: saque.valor, data: saque.dataProcessamento || saque.dataSolicitacao || saque.createdAt,
                      formaPagamento: saque.formaPagamento, observacao: saque.observacao, comprovanteUrl: saque.comprovanteUrl,
                      isEntrada: false,
                    });
                  });
                }
                extrato.sort((a, b) => new Date(b.data) - new Date(a.data));
                if (extrato.length === 0) return <div style={{ padding: 24, textAlign: 'center', color: T.label3, fontSize: 14 }}>Nenhuma movimentacao encontrada</div>;
                return extrato.map((item, i) => (
                  <div key={item.id} style={{ padding: 14, borderBottom: i < extrato.length - 1 ? `0.5px solid ${T.separator}` : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.label }}>{item.isEntrada ? 'Comissao Recebida' : 'Saque Realizado'}</div>
                        <div style={{ fontSize: 12, color: T.label2, marginTop: 2 }}>{item.tipoTexto}</div>
                        <div style={{ fontSize: 11, color: T.label3, marginTop: 2 }}>{new Date(item.data).toLocaleString('pt-BR')}</div>
                        {item.formaPagamento && <div style={{ fontSize: 11, color: T.label3 }}>Forma: {item.formaPagamento}</div>}
                        {item.observacao && <div style={{ fontSize: 11, color: T.label3 }}>Obs: {item.observacao}</div>}
                        {item.comprovanteUrl && (
                          <a href={item.comprovanteUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: T.accent, display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                            <ExternalLink size={11} /> Ver comprovante
                          </a>
                        )}
                      </div>
                      <div className="tnum" style={{ fontSize: 16, fontWeight: 700, color: item.isEntrada ? T.success : T.danger }}>
                        {item.isEntrada ? '+' : '-'} {fmtBRL(parseFloat(item.valor || 0))}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </SolidCard>
          </>
        )}
      </div>

      {/* Modal Solicitar Saque */}
      {showSolicitarSaque && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: T.surface, borderRadius: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflow: 'auto', boxShadow: T.shadowStrong, padding: 24 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.label, marginBottom: 16 }}>Solicitar Saque</div>
            <div style={{ fontSize: 13, color: T.label2, marginBottom: 12 }}>Saldo: {fmtBRL(saldoDisponivel)}</div>
            <input type="number" value={valorSaque} onChange={(e) => setValorSaque(e.target.value)}
              placeholder="Valor (minimo R$ 100)" min="100" style={{ ...inputStyle, marginBottom: 16 }} />
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 8 }}>Selfie *</label>
              {!selfieCapturada ? (
                !streamVideo ? (
                  <button onClick={iniciarCamera} style={{ width: '100%', padding: 14, borderRadius: 14, border: 'none', background: T.accent, color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Iniciar Camera</button>
                ) : (
                  <div>
                    <video ref={(ref) => { setVideoRef(ref); if (ref && streamVideo) ref.srcObject = streamVideo; }} autoPlay playsInline style={{ width: '100%', borderRadius: 14, border: `1px solid ${T.separator}`, maxHeight: 250, objectFit: 'cover' }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={capturarSelfie} style={{ flex: 1, padding: 12, borderRadius: 12, border: 'none', background: T.success, color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Capturar</button>
                      <button onClick={pararCamera} style={{ flex: 1, padding: 12, borderRadius: 12, border: 'none', background: T.fill, color: T.label, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
                    </div>
                  </div>
                )
              ) : (
                <div>
                  <img src={URL.createObjectURL(selfieCapturada)} alt="Selfie" style={{ width: '100%', borderRadius: 14, maxHeight: 250, objectFit: 'cover' }} />
                  <button onClick={() => { setSelfieCapturada(null); iniciarCamera(); }} style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 12, border: 'none', background: T.fill, color: T.label, fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Tirar Outra</button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowSolicitarSaque(false); setValorSaque(''); setSelfieCapturada(null); pararCamera(); }} style={{ flex: 1, padding: 15, borderRadius: 14, border: `1px solid ${T.separator}`, background: 'transparent', color: T.label, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleSolicitarSaque} disabled={!selfieCapturada} style={{ flex: 1, padding: 15, borderRadius: 14, border: 'none', background: !selfieCapturada ? T.fill2 : T.purple, color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: !selfieCapturada ? 'not-allowed' : 'pointer', boxShadow: !selfieCapturada ? 'none' : '0 4px 12px rgba(191,90,242,0.4)' }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinhasComissoesPage;
